/**
 * BellIcon — the notification bell + unread badge.
 *
 * Clicking the bell toggles the NotificationPanel. The badge shows the count
 * from useNotifications(), capping at "9+" so it doesn't overflow visually.
 */

import React, { useState, useRef, useEffect } from "react";

import { useNotifications } from "../../hooks/useNotifications";
import { useSupportUnread } from "../../hooks/useSupportUnread";
import { NotificationPanel } from "./NotificationPanel";

interface BellIconProps {
  /** Apply when the surrounding layout collapses (sidebar tucked). */
  compact?: boolean;
}

export const BellIcon: React.FC<BellIconProps> = ({ compact = false }) => {
  const { notifications, unreadCount, loading, loadingMore, hasMore, loadMore, markRead, markAllRead, dismiss, dismissAll } =
    useNotifications();
  const { summary: supportSummary } = useSupportUnread("clinic");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close panel on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const totalUnread = unreadCount + supportSummary.total;
  const displayCount = totalUnread > 9 ? "9+" : String(totalUnread);

  return (
    <div ref={wrapperRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        aria-label={`Notifications${totalUnread > 0 ? ` (${totalUnread} unread)` : ""}`}
        onClick={() => setIsOpen(o => !o)}
        style={{
          position: "relative",
          background: "transparent",
          border: "none",
          padding: 8,
          cursor: "pointer",
          borderRadius: 6,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Inline bell SVG — matches the existing icon library's stroke style */}
        <svg
          width={compact ? 20 : 22}
          height={compact ? 20 : 22}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>

        {totalUnread > 0 && (
          <span
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 8,
              background: "#dc2626",
              color: "white",
              fontSize: 10,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            {displayCount}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationPanel
          notifications={notifications}
          unreadCount={unreadCount}
          loading={loading}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onDismiss={dismiss}
          onDismissAll={dismissAll}
          onClose={() => setIsOpen(false)}
          onLoadMore={loadMore}
          support={supportSummary}
        />
      )}
    </div>
  );
};
